import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { approvePayroll, calculatePayroll, closePayroll, createPayrollPeriod, downloadHrFile, fetchPayrollPeriod, fetchPayrollPeriods, payPayrollEntry, submitPayroll } from "../../api/HrAPIs";
import { Empty, Field, HrFrame, HrGuard, StatusPill, inputClass, labelOf, money, useHr } from "./hrUi";

export default function PayrollPage() {
  return <HrGuard anyOf={["HR.ViewPayroll", "HR.ManagePayroll", "HR.ApprovePayroll"]}><Body /></HrGuard>;
}

function Body() {
  const hr = useHr();
  const queryClient = useQueryClient();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [selected, setSelected] = useState(null);
  const [useAttendance, setUseAttendance] = useState(true);
  const periods = useQuery({ queryKey: ["hrPeriods"], queryFn: fetchPayrollPeriods });
  const detail = useQuery({ queryKey: ["hrPeriod", selected], queryFn: () => fetchPayrollPeriod(selected), enabled: Boolean(selected) });

  const run = async (work) => {
    try { await work(); toast.success("تم"); queryClient.invalidateQueries({ queryKey: ["hrPeriods"] }); queryClient.invalidateQueries({ queryKey: ["hrPeriod", selected] }); }
    catch (error) { toast.error(error.message); }
  };

  const entries = detail.data?.entries || [];
  return (
    <HrFrame title="الرواتب" subtitle="كل فترة تحتفظ بلقطتها. الاعتماد يمنع إعادة الحساب الصامت.">
      {hr.can("HR.ManagePayroll") && (
        <form className="mb-4 flex flex-wrap items-end gap-2" onSubmit={(event) => { event.preventDefault(); run(async () => { const period = await createPayrollPeriod(year, month); setSelected(period.id); }); }}>
          <Field label="السنة"><input className={inputClass} type="number" value={year} onChange={(event) => setYear(Number(event.target.value))} /></Field>
          <Field label="الشهر"><input className={inputClass} type="number" min="1" max="12" value={month} onChange={(event) => setMonth(Number(event.target.value))} /></Field>
          <button className="rounded-xl bg-indigo-600 px-4 py-2 text-sm text-white">فتح فترة</button>
        </form>
      )}
      <div className="mb-4 flex flex-wrap gap-2">
        {(periods.data || []).map((period) => (
          <button key={period.id} type="button" onClick={() => setSelected(period.id)} className={`rounded-full px-3 py-1 text-sm ${selected === period.id ? "bg-indigo-600 text-white" : "bg-white"}`}>
            {period.month}/{period.year} · {labelOf(period.status)}
          </button>
        ))}
      </div>
      {selected && hr.can("HR.ManagePayroll") && (
        <div className="mb-4 flex flex-wrap gap-2">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={useAttendance} onChange={(event) => setUseAttendance(event.target.checked)} /> استخدام الحضور للأيام والساعات</label>
          <button type="button" className="rounded-xl border px-3 py-2 text-sm" onClick={async () => {
            try {
              const result = await calculatePayroll(selected, { useAttendance, employees: [] });
              toast.success(result.warnings?.length ? result.warnings.join(" · ") : "تم الحساب");
              queryClient.invalidateQueries({ queryKey: ["hrPeriod", selected] });
            } catch (error) { toast.error(error.message); }
          }}>حساب</button>
          <button type="button" className="rounded-xl border px-3 py-2 text-sm" onClick={() => run(() => submitPayroll(selected))}>إرسال للاعتماد</button>
          {hr.can("HR.ApprovePayroll") && <button type="button" className="rounded-xl border px-3 py-2 text-sm" onClick={() => run(() => approvePayroll(selected))}>اعتماد</button>}
          <button type="button" className="rounded-xl border px-3 py-2 text-sm" onClick={() => run(() => closePayroll(selected))}>إغلاق</button>
          <button type="button" className="rounded-xl border px-3 py-2 text-sm" onClick={() => downloadHrFile(`/payroll/periods/${selected}/export`, `payroll-${selected}.csv`).catch((error) => toast.error(error.message))}>Excel</button>
        </div>
      )}
      {detail.data?.warnings && <p className="mb-3 text-sm text-amber-700">{(detail.data.warnings || []).join(" · ")}</p>}
      {entries.length === 0 && selected && !detail.isLoading && <Empty text="لا توجد قيود في نطاق صلاحيتك." />}
      <div className="grid gap-3">
        {entries.map((entry) => <Entry key={entry.id} entry={entry} canPay={hr.can("HR.ManagePayroll")} onPaid={() => run(async () => {})} />)}
      </div>
      {(detail.data?.departments || []).length > 0 && (
        <div className="mt-4">
          <h2 className="mb-2 font-bold">ملخص الأقسام</h2>
          {detail.data.departments.map((row) => <p key={row.department} className="text-sm">{row.department}: {money(row.net)}</p>)}
        </div>
      )}
    </HrFrame>
  );
}

function Entry({ entry, canPay }) {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("تحويل");
  return (
    <article className="rounded-2xl border bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-bold">{entry.fullName}</p>
          <p className="text-sm text-slate-500">{entry.employeeCode} · {entry.departmentName} · {labelOf(entry.salaryType)}</p>
        </div>
        <StatusPill value={entry.status} />
      </div>
      <p className="mt-2 text-sm">استحقاقات {money(entry.totalEarnings)} · استقطاعات {money(entry.totalDeductions)} · سلف {money(entry.advanceRepayments)} · صافي {money(entry.netSalary)} · مدفوع {money(entry.paidAmount)}</p>
      <div className="mt-2 grid gap-1 text-xs text-slate-600">
        {(entry.lines || []).map((line) => <p key={line.id}>{line.name}: {line.quantity} × {line.rate} = {money(line.amount)}</p>)}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className="text-sm text-indigo-600" onClick={() => downloadHrFile(`/payroll/entries/${entry.id}/payslip`, `payslip-${entry.id}.pdf`).catch((error) => toast.error(error.message))}>قسيمة PDF</button>
        {canPay && (
          <form className="flex flex-wrap gap-2" onSubmit={async (event) => {
            event.preventDefault();
            try {
              await payPayrollEntry(entry.id, { amount: Number(amount), method });
              toast.success("تم تسجيل الدفعة");
              queryClient.invalidateQueries({ queryKey: ["hrPeriod"] });
            } catch (error) { toast.error(error.message); }
          }}>
            <input className={inputClass} type="number" step="0.01" placeholder="المبلغ" value={amount} onChange={(event) => setAmount(event.target.value)} />
            <input className={inputClass} value={method} onChange={(event) => setMethod(event.target.value)} />
            <button className="rounded-xl bg-slate-900 px-3 py-2 text-sm text-white">صرف</button>
          </form>
        )}
      </div>
    </article>
  );
}
